import React from 'react';
import { CheckIcon } from 'lucide-react';
import { Section } from './Shared';
import type { Volume } from '../types/product';
import { Bellflower, Blossom, Daisy, FlowerSpike } from './FloralDecor';
import { volumes } from '../data/volumes';
import { bonuses } from '../data/bonuses';
const volumeThemes: Record<Volume['theme'], {
  band: string;
  check: string;
  note: string;
}> = {
  green: {
    band: 'bg-emerald-800',
    check: 'bg-emerald-100 text-emerald-800',
    note: 'bg-emerald-50 text-emerald-900'
  },
  rose: {
    band: 'bg-rose-800',
    check: 'bg-rose-100 text-rose-800',
    note: 'bg-rose-50 text-rose-900'
  },
  plum: {
    band: 'bg-purple-900',
    check: 'bg-purple-100 text-purple-800',
    note: 'bg-purple-50 text-purple-900'
  }
};
const previewVideo = 'https://cdn.magicpatterns.com/uploads/eLce7vQq7KC2t8a3ziPb1v/Scroll_Bach_Optimizado_MP4_H264.mp4';
export function PackContents() {
  return <Section id="contents" className="relative" data-id="element-150" data-mp="11j526k36l46d">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden" data-id="element-151" data-mp="11kzp23y0j6d3">
        <Blossom className="absolute right-[4%] top-10 h-24 w-24" petalClassName="text-pink-200" centerClassName="text-amber-300" data-id="element-152" data-mp="3p7nyqun7ba2q" />
        <Daisy className="absolute left-[3%] top-12 h-16 w-16" petalClassName="text-amber-100" centerClassName="text-amber-300" data-id="element-153" data-mp="2sv3q0z2n038" />
        <Bellflower className="absolute left-[1%] top-[40%] hidden h-40 w-20 lg:block" petalClassName="text-violet-200" centerClassName="text-emerald-200" data-id="element-154" data-mp="3u56b6v0fg7fh" />
        <FlowerSpike className="absolute bottom-[18%] right-[1%] hidden h-40 w-12 lg:block" petalClassName="text-fuchsia-200" centerClassName="text-emerald-200" data-id="element-155" data-mp="6t59pdmv5qi3" />
      </div>

      <div className="mx-auto mb-16 max-w-4xl text-center md:mb-20" data-id="element-156" data-mp="gpi0a2n43o8m">
        <h2 className="text-3xl font-bold tracking-tight text-emerald-900 md:text-5xl" data-id="element-157" data-mp="2nitzlc2udiph">Take a look inside</h2>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-stone-700 md:text-lg" data-id="element-158" data-mp="2ikioky0t925o">
          A short preview of the pages you’ll find inside the guide once you get access.
        </p>

        <figure className="mx-auto mt-8 max-w-3xl" data-id="element-159" data-mp="3c96v5nd0pkkl">
          <div className="overflow-hidden rounded-xl border-2 border-amber-500/70 bg-white p-2 shadow-md" data-id="element-160" data-mp="3h78hch5qq00a">
            <video src={previewVideo} className="mx-auto block h-auto max-h-[78vh] w-full rounded-lg bg-white object-contain" autoPlay muted loop playsInline controls preload="metadata" aria-label="Video preview scrolling through the inside pages of the Bach Flower Consultation Guide" data-id="element-161" data-mp="3m74lqhicntgd" />
          </div>
          <figcaption className="mt-3 text-xs text-stone-500" data-id="element-162" data-mp="8r6lm3rtd74v">
            Preview of the digital PDF pages. Content shown is a sample of the full guide.
          </figcaption>
        </figure>
      </div>

      <h2 className="mb-8 text-center text-2xl font-bold tracking-tight text-emerald-900 md:text-4xl" data-id="element-163" data-mp="iqe22ztuhhw7">
        What’s included
      </h2>

      <div className="mx-auto grid max-w-6xl gap-4 md:grid-cols-3 md:gap-5" data-id="element-164" data-mp="1y9dbw4haz4qc">
        {volumes.map(volume => {
        const theme = volumeThemes[volume.theme];
        return <article key={volume.label} className="flex h-full flex-col overflow-hidden rounded-xl border border-amber-400/70 bg-white shadow-md" data-id="element-165" data-mp="1oaxj7x2mnneq">
              <div className={`relative flex gap-4 overflow-hidden p-4 ${theme.band}`} data-id="element-166" data-mp="2m0ing7g7nauu">
                <Blossom className="pointer-events-none absolute -right-5 -top-5 h-20 w-20" petalClassName="text-white/10" centerClassName="text-amber-300/30" data-id="element-167" data-mp="2h1o5f7q2hqbs" />
                <div className="relative w-20 shrink-0 self-start overflow-hidden rounded-sm shadow-lg ring-2 ring-amber-300/80" data-id="element-168" data-mp="34ow3b4l61x3s">
                  <img src={volume.cover} alt={volume.coverAlt} loading="lazy" className="block h-auto w-full" draggable={false} data-id="element-169" data-mp="2zxnqdykmntlv" />
                </div>
                <div className="relative min-w-0" data-id="element-170" data-mp="2airtql3e1psl">
                  <span className="inline-block rounded-full bg-amber-300 px-2 py-0.5 text-[10px] font-bold tracking-[.16em] text-stone-900" data-id="element-171" data-mp="2ak2v6e9d2lgj">
                    {volume.label}
                  </span>
                  <h3 className="mt-2 text-base font-bold leading-snug text-white" data-id="element-172" data-mp="337p8wfcaz6h4">{volume.title}</h3>
                  <p className="mt-1 text-xs leading-snug text-white/80" data-id="element-173" data-mp="388395b7nxc4l">{volume.summary}</p>
                </div>
              </div>
              <div className="flex flex-1 flex-col p-4" data-id="element-174" data-mp="2yfxkaacpjatm">
                <ul className="space-y-2 text-sm leading-snug text-stone-900" data-id="element-175" data-mp="2yee2ugfbetq9">
                  {volume.features.map(feature => <li key={feature} className="flex gap-2.5" data-id="element-176" data-mp="3qwi2441jj081">
                      <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${theme.check}`} aria-hidden="true" data-id="element-177" data-mp="3qxt3xybfdxwi">
                        <CheckIcon className="h-3 w-3" strokeWidth={3} data-id="element-178" data-mp="1aglmiqjuq1ep" />
                      </span>
                      <span data-id="element-179" data-mp="1p95b8m6986cm">{feature}</span>
                    </li>)}
                </ul>
                {volume.note && <div className="mt-auto pt-4" data-id="element-180" data-mp="wjq0lxii4fh8">
                    <p className={`rounded-md px-3 py-2 text-xs italic leading-snug ${theme.note}`} data-id="element-181" data-mp="wkhwzajxah8k">{volume.note}</p>
                  </div>}
              </div>
            </article>;
      })}
      </div>

      <div className="mb-8 mt-16 text-center md:mt-20" data-id="element-182" data-mp="11h8du03jxgv1">
        <h3 className="text-2xl font-bold tracking-tight text-emerald-900 md:text-3xl" data-id="element-183" data-mp="11jujtvasnob5">
          5 practical bonuses — <span className="text-amber-800" data-id="element-184" data-mp="2h0cubexkw04b">included with the guide</span>
        </h3>
      </div>

      <ul className="mx-auto grid max-w-6xl gap-3 sm:grid-cols-2 lg:grid-cols-5" data-id="element-185" data-mp="2cz9eutjg063m">
        {bonuses.map(bonus => <li key={bonus.number} className="flex gap-3 rounded-xl border border-stone-200 bg-white p-3 shadow-sm lg:flex-col lg:gap-0 lg:p-4" data-id="element-186" data-mp="1el3ybhu089pw">
            <div className="w-14 shrink-0 lg:mx-auto lg:mb-3 lg:w-24" data-id="element-187" data-mp="1ji2rny5ugcn1">
              <img src={bonus.cover} alt={`Cover of Bonus ${bonus.number}: ${bonus.title}`} loading="lazy" className="h-auto w-full drop-shadow-md" draggable={false} data-id="element-188" data-mp="1nl8s2p69z9ll" />
            </div>
            <div className="flex min-w-0 flex-1 flex-col" data-id="element-189" data-mp="zv4zrdyjew9w">
              <p className="text-[10px] font-semibold tracking-[.18em] text-amber-800" data-id="element-190" data-mp="14mwfsuk2hjls">BONUS {bonus.number}</p>
              <h4 className="mt-0.5 text-sm font-bold leading-snug text-emerald-900" data-id="element-191" data-mp="859x6flo5a3a">{bonus.title}</h4>
              <p className="mt-1 text-xs leading-snug text-stone-700" data-id="element-192" data-mp="c6dbuyrzd02x">{bonus.description}</p>
              <p className="mt-auto pt-1.5 text-[11px] italic leading-snug text-stone-500" data-id="element-193" data-mp="4muefnhzp4at">{bonus.note}</p>
            </div>
          </li>)}
      </ul>

      <p className="mt-6 text-center text-sm font-medium text-stone-700" data-id="element-194" data-mp="3vu6uu0b1tdpo">
        Five bonuses · 30 pages each · <span className="font-semibold text-emerald-700" data-id="element-195" data-mp="3rtwr6uaqazdz">150 pages of additional resources</span>
      </p>
    </Section>;
}